import { combineEpics, Epic } from 'redux-observable';
import { from, of } from 'rxjs';
import {
  switchMap,
  map,
  startWith,
  catchError,
  filter, tap
} from 'rxjs/operators';
import { isOfType } from 'typesafe-actions';
import { Position, Toaster, Intent } from '@blueprintjs/core';

import {
  IntegrationActions,
  IntegrationActionTypes,
  loadingIntegration,
  loadedIntegration,
  loadingIntegrationFailed,
  loadingIntegrations,
  loadedIntegrations,
  loadingIntegrationsFailed,
  createdIntegration,
  creatingIntegration,
  creatingIntegrationFailed,
  updatedIntegration,
  updatingIntegration,
  updatingIntegrationFailed,
  deletedIntegration,
  deletingIntegration,
  deletingIntegrationFailed,
  testIntegrationSuccess,
  testIntegrationFailure,
} from '../actions/integrationActions';
import { IState } from '../reducers';
import {
  getIntegration,
  getIntegrations,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  testIntegration,
} from '../../services/integrationApi';

export const integrationToaster = Toaster.create({
  className: 'integration-toaster',
  position: Position.TOP
});

const loadIntegrationEpic: Epic<IntegrationActions, IntegrationActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(IntegrationActionTypes.LOAD_INTEGRATION)),
    switchMap((action: any) =>
      from(getIntegration(action.payload)).pipe(
        map((response: any) => loadedIntegration(response.result)),
        startWith(loadingIntegration()),
        catchError((error: any) => {
          return of(loadingIntegrationFailed());
        })
      )
    )
  );

const loadIntegrationsEpic: Epic<IntegrationActions, IntegrationActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(IntegrationActionTypes.LOAD_INTEGRATIONS)),
    switchMap((action: any) =>
      from(getIntegrations({
          filters: action.payload.filters,
          page: action.payload.page,
          page_size: action.payload.page_size
        }, action.payload.sort, action.payload.source)).pipe(
        map((response: any) => loadedIntegrations(
          response.result,
          action.payload.reload,
          action.payload.page_size,
        )),
        startWith(loadingIntegrations()),
        catchError((error: any) => {
          return of(loadingIntegrationsFailed());
        })
      )
    )
  );

const createIntegrationEpic: Epic<IntegrationActions, IntegrationActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(IntegrationActionTypes.CREATE_INTEGRATION)),
    switchMap((action: any) =>
      from(createIntegration(action.payload)).pipe(
        map((response: any) => createdIntegration(response.result)),
        startWith(creatingIntegration()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          integrationToaster.show({
            message: `An error occurred while creating: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(creatingIntegrationFailed());
        })
      )
    )
  );

const updateIntegrationEpic: Epic<IntegrationActions, IntegrationActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(IntegrationActionTypes.UPDATE_INTEGRATION)),
    switchMap((action: any) =>
      from(updateIntegration(action.payload.uid as string, action.payload)).pipe(
        map((response: any) => updatedIntegration({
          ...response.result,
          uid: action.payload.uid
        })),
        startWith(updatingIntegration()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          integrationToaster.show({
            message: `An error occurred while updating: ${message}.`,
            intent: Intent.DANGER
          });

          return of(updatingIntegrationFailed());
        })
      )
    )
  );

const deleteIntegrationEpic: Epic<IntegrationActions, IntegrationActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(IntegrationActionTypes.DELETE_INTEGRATION)),
    switchMap((action: any) =>
      from(deleteIntegration(action.payload)).pipe(
        map((response: any) => {
          if (response?.message === 'OK') {
            integrationToaster.show({
              message: `Integration successfully deleted.`,
              intent: Intent.SUCCESS
            });
          }
          return deletedIntegration(action.payload);
        }),
        startWith(deletingIntegration()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          integrationToaster.show({
            message: `An error occurred while deleting: ${message}.`,
            intent: Intent.DANGER
          });

          return of(deletingIntegrationFailed());
        })
      )
    )
  );

const testIntegrationEpic: Epic<IntegrationActions, IntegrationActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(IntegrationActionTypes.TEST_INTEGRATION_REQUEST)),
    switchMap((action: any) =>
      from(testIntegration(action.payload.Integration)).pipe(
        tap((response: any) => {
          const message = response.message || "Successfully connected to integration.";
          integrationToaster.show({
            message: message,
            intent: Intent.SUCCESS
          });
          return testIntegrationSuccess();
        }),
        map((response: any) => testIntegrationSuccess()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "An error occurred while testing integration.";

          integrationToaster.show({
            message: message,
            intent: Intent.DANGER
          });
          return of(testIntegrationFailure());
        })
      )
    )
  );

export default combineEpics(
  loadIntegrationEpic,
  loadIntegrationsEpic,
  createIntegrationEpic,
  updateIntegrationEpic,
  deleteIntegrationEpic,
  testIntegrationEpic,
);
